import { createShowcaseProjects } from './showcaseCatalog';

/**
 * demoStore.ts - Centralized Demo Mode State Management
 * 
 * Single source of truth for all demo mode data that needs to be shared
 * across multiple hooks and services. This prevents inconsistencies when
 * one component creates data and another needs to read it.
 * 
 * Architecture:
 * - All demo state lives here (not in individual hooks/services)
 * - Provides typed functions for CRUD operations
 * - Supports subscriptions for reactive updates
 * - Session-persistent (clears on page refresh)
 * 
 * @see CompanyService.ts - uses this for demo company operations
 * @see useCompany.ts - uses this for linked projects
 * @see CreateProjectDrawer.tsx - uses this when linking company to project
 */

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface DemoProjectCompanyLink {
  id: string;
  project_id: string;
  project_name: string;
  project_status: 'active' | 'planning' | 'completed' | 'on-hold' | 'cancelled';
  company_id: string;
  relationship_type: 'internal' | 'capital_recipient' | 'partner' | 'investor' | 'vendor';
  engagement_type: 'primary' | 'secondary' | 'advisory' | 'observer';
  status: 'active' | 'inactive' | 'pending' | 'completed';
  /** If true, members of this company can see other companies linked to this project */
  can_view_company_list: boolean;
  effective_from?: string;
  effective_to?: string;
  created_at: string;
}

type Listener = () => void;

// ─────────────────────────────────────────────────────────────────────────────
// INITIAL DEMO DATA
// ─────────────────────────────────────────────────────────────────────────────

const SHOWCASE_CREATED_AT = '2026-07-20T10:00:00.000Z';

const toDemoProjectStatus = (status: string): DemoProjectCompanyLink['project_status'] => {
  if (status === 'planning' || status === 'completed' || status === 'cancelled') return status;
  if (status === 'on_hold') return 'on-hold';
  return 'active';
};

const INITIAL_PROJECT_COMPANY_LINKS: DemoProjectCompanyLink[] = createShowcaseProjects().flatMap((project) => {
  // Meridian Advisory: Partner on most programmes; Investee (capital_recipient) on
  // Harbour District (project "2") so company_representative sidebar labels can
  // switch with active project participation context.
  const meridianRelationship: DemoProjectCompanyLink['relationship_type'] =
    project.id === '2' ? 'capital_recipient' : 'partner';
  const meridianEngagement: DemoProjectCompanyLink['engagement_type'] =
    project.id === '2' ? 'secondary' : 'advisory';

  const participants: Array<Pick<DemoProjectCompanyLink, 'company_id' | 'relationship_type' | 'engagement_type' | 'can_view_company_list'>> = [
    { company_id: 'showcase-company-ax1', relationship_type: 'internal', engagement_type: 'primary', can_view_company_list: true },
    { company_id: project.primaryClientCompany?.id ?? 'showcase-company-recipient', relationship_type: 'capital_recipient', engagement_type: 'primary', can_view_company_list: true },
    {
      company_id: 'showcase-company-partner',
      relationship_type: meridianRelationship,
      engagement_type: meridianEngagement,
      can_view_company_list: true,
    },
    { company_id: 'showcase-company-vendor', relationship_type: 'vendor', engagement_type: 'secondary', can_view_company_list: false },
    { company_id: 'showcase-company-investor', relationship_type: 'investor', engagement_type: 'observer', can_view_company_list: false },
  ];

  return participants.map((participant) => ({
    id: `showcase-link-${project.id}-${participant.company_id}-${participant.relationship_type}`,
    project_id: project.id,
    project_name: project.name,
    project_status: toDemoProjectStatus(project.status),
    ...participant,
    status: project.status === 'completed' ? 'completed' : 'active',
    effective_from: project.startDate.toISOString(),
    effective_to: project.endDate?.toISOString(),
    created_at: SHOWCASE_CREATED_AT,
  }));
});

// ─────────────────────────────────────────────────────────────────────────────
// STATE (private, session-persistent)
// ─────────────────────────────────────────────────────────────────────────────

// Project ↔ Company links
let projectCompanyLinks: DemoProjectCompanyLink[] = [...INITIAL_PROJECT_COMPANY_LINKS];

// Subscribers for reactive updates
const listeners: Set<Listener> = new Set();

// ─────────────────────────────────────────────────────────────────────────────
// SUBSCRIPTION API (for reactive UI updates)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Subscribe to store changes
 * Returns unsubscribe function
 */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Notify all subscribers of state change
 */
function notifyListeners(): void {
  listeners.forEach(listener => listener());
}

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT ↔ COMPANY LINK API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all links for a specific company
 * Ordered by created_at descending (newest first)
 */
export function getCompanyLinkedProjects(
  companyId: string,
  options?: { includeInactive?: boolean }
): DemoProjectCompanyLink[] {
  return projectCompanyLinks
    .filter(link => {
      if (link.company_id !== companyId) return false;
      if (!options?.includeInactive && link.status !== 'active') return false;
      return true;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

/**
 * Get all links for a specific project
 * Ordered by relationship_type priority (capital_recipient first, then partner, etc.)
 */
export function getProjectLinkedCompanies(
  projectId: string,
  options?: { includeInactive?: boolean }
): DemoProjectCompanyLink[] {
  const typePriority: Record<string, number> = {
    'capital_recipient': 1,
    'internal': 2,
    'partner': 3,
    'investor': 4,
    'vendor': 5
  };
  
  return projectCompanyLinks
    .filter(link => {
      if (link.project_id !== projectId) return false;
      if (!options?.includeInactive && link.status !== 'active') return false;
      return true;
    })
    .sort((a, b) => (typePriority[a.relationship_type] || 99) - (typePriority[b.relationship_type] || 99));
}

/**
 * Get the primary capital recipient company for a project
 * Returns null if no primary capital recipient exists
 */
export function getPrimaryClientCompany(projectId: string): DemoProjectCompanyLink | null {
  return projectCompanyLinks.find(link => 
    link.project_id === projectId &&
    link.relationship_type === 'capital_recipient' &&
    link.engagement_type === 'primary' &&
    link.status === 'active'
  ) || null;
}

/**
 * Add a new project ↔ company link
 * Returns the created link
 */
export function addProjectCompanyLink(data: {
  projectId: string;
  projectName: string;
  projectStatus?: 'active' | 'planning' | 'completed' | 'on-hold' | 'cancelled';
  companyId: string;
  relationshipType?: 'internal' | 'capital_recipient' | 'partner' | 'investor' | 'vendor';
  engagementType?: 'primary' | 'secondary' | 'advisory' | 'observer';
  /** Allow this company to see other companies linked to the project */
  canViewCompanyList?: boolean;
}): DemoProjectCompanyLink {
  // Check for duplicate
  const existing = projectCompanyLinks.find(link =>
    link.project_id === data.projectId &&
    link.company_id === data.companyId &&
    link.relationship_type === (data.relationshipType || 'capital_recipient')
  );
  
  if (existing) {
    console.log('[DEMO] Link already exists:', existing.id);
    return existing;
  }

  // Default can_view_company_list based on relationship type:
  // - internal/capital_recipient: true (collaborative)
  // - partner/investor/vendor: false (restricted)
  const defaultCanView = ['internal', 'capital_recipient'].includes(data.relationshipType || 'capital_recipient');
  
  const newLink: DemoProjectCompanyLink = {
    id: `demo-link-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    project_id: data.projectId,
    project_name: data.projectName,
    project_status: data.projectStatus || 'active',
    company_id: data.companyId,
    relationship_type: data.relationshipType || 'capital_recipient',
    engagement_type: data.engagementType || 'primary',
    status: 'active',
    can_view_company_list: data.canViewCompanyList ?? defaultCanView,
    created_at: new Date().toISOString()
  };
  
  projectCompanyLinks = [newLink, ...projectCompanyLinks];
  notifyListeners();
  
  console.log('[DEMO] Added project-company link:', newLink.id);
  return newLink;
}

/**
 * Remove a project ↔ company link by ID
 */
export function removeProjectCompanyLink(linkId: string): boolean {
  const initialLength = projectCompanyLinks.length;
  projectCompanyLinks = projectCompanyLinks.filter(link => link.id !== linkId);
  
  if (projectCompanyLinks.length !== initialLength) {
    notifyListeners();
    console.log('[DEMO] Removed project-company link:', linkId);
    return true;
  }
  
  return false;
}

/**
 * Update a project ↔ company link
 */
export function updateProjectCompanyLink(
  linkId: string,
  updates: Partial<Omit<DemoProjectCompanyLink, 'id' | 'created_at'>>
): DemoProjectCompanyLink | null {
  const index = projectCompanyLinks.findIndex(link => link.id === linkId);
  
  if (index === -1) return null;
  
  projectCompanyLinks[index] = {
    ...projectCompanyLinks[index],
    ...updates
  };
  
  notifyListeners();
  console.log('[DEMO] Updated project-company link:', linkId);
  return projectCompanyLinks[index];
}

// ─────────────────────────────────────────────────────────────────────────────
// RESET (for testing)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reset demo store to initial state
 * Useful for testing or "start fresh" functionality
 */
export function resetDemoStore(): void {
  projectCompanyLinks = [...INITIAL_PROJECT_COMPANY_LINKS];
  notifyListeners();
  console.log('[DEMO] Store reset to initial state');
}

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get current state snapshot (for debugging)
 */
export function getDebugSnapshot(): { projectCompanyLinks: DemoProjectCompanyLink[] } {
  return {
    projectCompanyLinks: [...projectCompanyLinks]
  };
}
